/**
 * Pricing — full pricing page.
 *
 * Data sources (all bound at runtime, NO hardcoded prices in this file):
 *   - `/api/config/pricing` via `useConfig().pricing` for the per-dog
 *     base price, recurring discount %, and securement fee.
 *   - `/api/public/customer-plans` for the Plus / Premium / Free
 *     plan cards (price_monthly, discount_pct, perks).
 *   - `/api/public/partner-tiers` for the Standard / Preferred / Elite
 *     tier cards (price_monthly, commission_pct).
 *
 * Copy: every editorial string lives in `@/content/pricing`. JSX is a
 * pure layout layer that consumes named exports.
 *
 * Failure surface: if either of the two public endpoints fails, an
 * inline error banner renders in place of the affected grid with a
 * Retry button — we never fall back to fabricated numbers.
 *
 * Section order:
 *   1. HERO BAND
 *   2. CUSTOMER BASE PRICING (3 callouts: per-dog / recurring / securement)
 *   3. CUSTOMER PLANS (live)
 *   4. PARTNER COMMISSION TIERS (live)
 *   5. TRUST & SAFETY (Stripe-anchored verification)
 *   6. FAQ
 *   7. CTA BAND
 */
import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Lock,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import PublicPageShell from "@/components/PublicPageShell";
import PartnerTiersPanel from "@/components/PartnerTiersPanel";
import BrandDivider from "@/components/BrandDivider";
import SavingsPreviewChip from "@/components/SavingsPreviewChip";
import WinnerSlotCard from "@/components/WinnerSlotCard";
import { useConfig } from "@/context/ConfigContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import QuoteTool from "@/components/QuoteTool";
import { AccentHeadline } from "@/lib/AccentHeadline";
import {
  PRICING_HERO,
  PRICING_BASE,
  PRICING_QUOTE,
  PRICING_TRUST,
  PRICING_FAQ,
  PRICING_CTA,
} from "@/content/pricing";

/* ---------------------------------------------------------------------------
   Icon lookup — content modules reference icons by name (string) so the
   CMS layer stays serialisable.
   --------------------------------------------------------------------------- */
const TRUST_ICONS = {
  ShieldCheck,
  Lock,
  FileText,
};

/* ---------------------------------------------------------------------------
   Helpers
   --------------------------------------------------------------------------- */
const fmtMoney = (n) =>
  n === 0 || n === 0.0
    ? "$0"
    : n % 1 === 0
      ? `$${n.toFixed(0)}`
      : `$${n.toFixed(2)}`;

/* ---------------------------------------------------------------------------
   Page
   --------------------------------------------------------------------------- */
export default function Pricing() {
  const { pricing } = useConfig();
  // Item N — tiered model: `base_price` for first dog + `additional_dog_price`
  // for each extra pup. Fallbacks used ONLY at initial mount before the
  // config-fetch resolves; the interpolation tests prove the rendered
  // copy tracks whatever config the endpoint returns.
  const basePrice = pricing.default_base_price ?? 20;
  const additionalDogPrice = pricing.default_additional_dog_price ?? 5;
  const recurringPct = pricing.default_recurring_discount_pct ?? 0;
  const securement = pricing.securement_fee ?? 0;

  // Phase L.1 — Removed the LIGHT_SCOPE_STYLE token override. Pricing
  // now inherits the global theme (light OR dark) from ThemeProvider's
  // data-theme attribute cascade. The section-surface value flips from
  // the prior local `#EEF1F3` to L's `--pp-v3-surface` (white in light,
  // charcoal in dark). If a specific cool-grey Pricing shade is needed
  // for L.2 marketing polish, add `--pp-section-cool-grey` to
  // theme/tokens.js as a per-theme token and consume via Tailwind.

  // Audience toggle — default "owners". Layout/visibility only; the
  // partner-tier surface renders from the shared `<PartnerTiersPanel>`
  // component whose copy is locked to the spec, so switching views is
  // a state-only flip with zero fetch latency. URL-param sync was
  // intentionally skipped for this dispatch (back/forward and
  // default-URL noise risks outweighed the share-link benefit).
  // Partner-first launch posture: the audience toggle defaults to
  // the PooPartners view so recruiting traffic lands on their pane
  // without a click. Dog-owner segment remains one tap away.
  const [view, setView] = useState("partners");

  // Phase H.1 — QuoteTool inputs bridged to <SavingsPreviewChip>. The
  // chip debounces its own /quote refetches internally so this
  // callback fires cheaply on every input change.
  const [quoteInputs, setQuoteInputs] = useState(null);
  const handleQuoteInputsChange = useCallback((next) => {
    setQuoteInputs(next);
  }, []);

  // Tier-CTA busy state — mirrors the PartnerTiersPage handler so
  // Preferred / Elite POST /api/partner/tier/select shows a spinner
  // on the correct card while the request is in flight.
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busyTierId, setBusyTierId] = useState(null);
  const isAuthedPartner = !!user && !!user.is_provider;

  async function handleSelectableTier(tierId) {
    if (!isAuthedPartner) {
      navigate(`/partner/apply?intent=${tierId}`);
      return;
    }
    setBusyTierId(tierId);
    try {
      await api.post("/partner/tier/select", { tier_id: tierId });
      toast.success(
        "Selection saved. Billing dispatch is coming soon — no charge yet.",
        { duration: 4200 },
      );
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) ||
                  "Could not save your tier selection. Please try again.";
      toast.error(msg);
    } finally {
      setBusyTierId(null);
    }
  }

  function handleTierCta(tierId) {
    if (tierId === "standard") {
      navigate("/partner/apply");
      return;
    }
    if (tierId === "enterprise") {
      navigate("/contact?source=enterprise");
      return;
    }
    handleSelectableTier(tierId);
  }

  return (
    <PublicPageShell>
     <div data-testid="pricing-page-root">
      {/* =========================== HERO =========================== */}
      <section data-testid="pricing-hero-section">
        <div className="pp-wrap py-12 md:py-20 text-center">
          <span className="pp-eyebrow" data-testid="pricing-hero-eyebrow">
            <span className="dot" />
            {PRICING_HERO.eyebrow}
          </span>
          <h1
            className="pp-h1 mt-6 mx-auto max-w-3xl"
            data-testid="pricing-hero-headline"
          >
            {/* Item 14 refinement: migrated from legacy
               `{headline_primary}{" "}<span class="acc">{headline_accent}</span>`
               (which colored an entire half-sentence via the split-
               halves content shape) to the canonical phrase-accent
               shape via `<AccentHeadline>`. Accent is now "your pack"
               inside "Pricing tailored to your pack." — same
               mechanism every section H2 on this page uses. */}
            <AccentHeadline
              text={PRICING_HERO.headline_primary}
              accent={PRICING_HERO.headline_accent}
            />
          </h1>
          <p
            className="pp-sub mt-5 mx-auto max-w-2xl"
            data-testid="pricing-hero-sub"
          >
            {PRICING_HERO.intro}
          </p>
        </div>
      </section>

      {/* ===================== AUDIENCE TOGGLE =====================
          Segmented pill — switches the visible content sections.
          Both `/api/public/*` fetches above still fire on mount, so the
          toggle is a state-only flip with zero perceptible latency. */}
      <BrandDivider testId="pricing-divider-above-toggle" />
      <div className="pp-wrap pt-2 pb-6 md:pb-8 flex justify-center">
        <AudienceToggle view={view} onChange={setView} />
      </div>
      {/* Customer-audience savings preview chip. Renders only when
          the audience toggle is on the customer view; hidden entirely
          on the PooPartner view. Sample payload documented in the
          component (1 dog / weekly / not-first).
          Rendered percent is DERIVED per-booking from the engine's
          `/quote` response — never a flat tier-max literal. The
          commission-cap clamp can reduce the effective rate below
          the tier max on small bookings; the chip must reflect the
          real per-booking rate. Static-source guards on the chip
          file forbid hardcoded rate literals.
          The chip is reactive to live QuoteTool inputs; `quoteInputs`
          state above forwards dog_count/frequency changes. Falls
          back to the fixed sample when inputs are incomplete. */}
      {view === "owners" && <SavingsPreviewChip quoteInputs={quoteInputs} />}

      {/* ====================== CUSTOMER BASE PRICING ====================== */}
      {view === "owners" && (
      <section
        style={{ background: "var(--pp-v3-surface)" }}
        data-testid="pricing-base-section"
      >
        <div className="pp-wrap py-16 md:py-20">
          <div className="max-w-2xl">
            <span className="pp-kick" data-testid="pricing-base-kick">
              {PRICING_BASE.kick}
            </span>
            <h2 className="pp-h2 mt-3" data-testid="pricing-base-headline">
              <AccentHeadline
                text={PRICING_BASE.headline}
                accent={PRICING_BASE.headline_accent}
              />
            </h2>
            <p
              className="pp-customer-p mt-4"
              data-testid="pricing-base-body"
            >
              {PRICING_BASE.body}
            </p>
          </div>

          <ul
            className="mt-10 grid gap-6 md:gap-8 list-none m-0 p-0"
            data-testid="pricing-base-grid"
          >
            <li
              className="rounded-[20px] border border-pp-line bg-pp-v3-bg p-7 max-w-2xl"
              style={{ boxShadow: "var(--pp-shadow)" }}
              data-testid="pricing-base-card-perdog"
            >
              <span
                className="inline-flex items-center justify-center rounded-[10px] bg-pp-lavender"
                style={{ width: 40, height: 40 }}
                aria-hidden="true"
              >
                <Sparkles
                  className="h-5 w-5"
                  style={{ color: "var(--pp-lavender-text)" }}
                  strokeWidth={2}
                />
              </span>
              <p className="mt-5 text-[13px] font-bold uppercase tracking-[0.08em] text-pp-text-soft">
                {PRICING_BASE.per_dog_card.label}
              </p>
              <p
                className="mt-2 text-[34px] font-extrabold text-pp-slate leading-tight"
                data-testid="pricing-base-perdog-value"
              >
                {PRICING_BASE.per_dog_card.first_dog_prefix}{" "}
                <span data-testid="pricing-base-first-dog-price">
                  {fmtMoney(basePrice)}
                </span>
              </p>
              <p
                className="mt-1 text-[20px] font-bold text-pp-slate leading-tight"
                data-testid="pricing-base-additional-dog-line"
              >
                {PRICING_BASE.per_dog_card.additional_prefix}{" "}
                <span
                  className="text-pp-orange"
                  data-testid="pricing-base-additional-dog-price"
                >
                  +{fmtMoney(additionalDogPrice)}
                </span>
              </p>
              <p className="mt-3 text-[15px] text-pp-text-soft leading-relaxed">
                {PRICING_BASE.per_dog_card.body}
              </p>
            </li>
          </ul>

          <p
            className="mt-8 text-[15px] text-pp-text-soft leading-relaxed max-w-2xl"
            data-testid="pricing-base-reassurance"
          >
            {PRICING_BASE.reassurance}
          </p>
        </div>
      </section>
      )}

      {/* ========================= BUILD YOUR PACK ========================= */}
      {view === "owners" && (
      <section data-testid="pricing-quote-section">
        <div className="pp-wrap py-16 md:py-24">
          <div className="max-w-2xl">
            <span className="pp-kick" data-testid="pricing-quote-kick">
              {PRICING_QUOTE.kick}
            </span>
            <h2 className="pp-h2 mt-3" data-testid="pricing-quote-headline">
              <AccentHeadline
                text={PRICING_QUOTE.headline}
                accent={PRICING_QUOTE.headline_accent}
              />
            </h2>
            <p
              className="pp-customer-p mt-4"
              data-testid="pricing-quote-body"
            >
              {PRICING_QUOTE.body}
            </p>
          </div>

          <div className="mt-12">
            <QuoteTool
              testId="pricing-quote-tool"
              onInputsChange={handleQuoteInputsChange}
            />
          </div>
        </div>
      </section>
      )}

      {/* ===================== PARTNER COMMISSION TIERS ===================== */}
      {view === "partners" && (
      <section
        style={{ background: "var(--pp-v3-surface)" }}
        data-testid="pricing-tiers-section"
      >
        <div className="pp-wrap py-16 md:py-24">
          {/* The tier-cards + Level Up ladder + payout table now live
              in the shared `<PartnerTiersPanel>` — same surface the
              dedicated `/partner/tiers` route renders. Copy invariants
              (zero literal "%", 4 cards incl. Enterprise "Talk to us",
              spec-locked visuals) are enforced by
              `Pricing.partnerTab.copyAudit.test.jsx` +
              `PartnerTiersPage.copyAudit.test.jsx`. */}
          <PartnerTiersPanel
            onTierCta={handleTierCta}
            busyTierId={busyTierId}
          />
        </div>
      </section>
      )}

      {/* ========================== TRUST & SAFETY ========================== */}
      <section
        data-testid="pricing-trust-section"
        aria-labelledby="pricing-trust-headline"
      >
        <div className="pp-wrap py-16 md:py-24">
          <div className="max-w-2xl">
            <span className="pp-kick" data-testid="pricing-trust-kick">
              {PRICING_TRUST.kick}
            </span>
            <h2
              id="pricing-trust-headline"
              className="pp-h2 mt-3"
              data-testid="pricing-trust-headline"
            >
              <AccentHeadline
                text={PRICING_TRUST.headline}
                accent={PRICING_TRUST.headline_accent}
              />
            </h2>
            <p
              className="pp-customer-p mt-4"
              data-testid="pricing-trust-body"
            >
              {PRICING_TRUST.body}
            </p>
          </div>

          <ul className="mt-12 grid gap-6 md:grid-cols-3 md:gap-8 list-none m-0 p-0">
            {PRICING_TRUST.items.map((item, i) => {
              const Icon = TRUST_ICONS[item.icon] || ShieldCheck;
              const titleId = `pricing-trust-title-${i + 1}`;
              const noteId = `pricing-trust-note-id-${i + 1}`;
              return (
                <li
                  key={item.key}
                  className="rounded-[20px] border border-pp-line bg-pp-v3-bg p-7"
                  style={{ boxShadow: "var(--pp-shadow)" }}
                  data-testid={`pricing-trust-item-${i + 1}`}
                >
                  <span
                    className="inline-flex items-center justify-center rounded-[10px] bg-pp-lavender"
                    style={{ width: 40, height: 40 }}
                    aria-hidden="true"
                  >
                    <Icon
                      className="h-5 w-5"
                      style={{ color: "var(--pp-lavender-text)" }}
                      strokeWidth={2}
                    />
                  </span>
                  {/* Phase L.2 P3 — audit P-2. The trust note (e.g.
                      "Powered by Stripe") is programmatically related
                      to its `<h3>` via `aria-describedby` so SR users
                      hear the note as a description of the title
                      instead of a floating badge. */}
                  <h3
                    id={titleId}
                    className="mt-5 text-[18px] font-bold text-pp-slate"
                    aria-describedby={noteId}
                  >
                    {item.title}
                  </h3>
                  <p className="mt-2 text-[15px] text-pp-text-soft leading-relaxed">
                    {item.body}
                  </p>
                  <span
                    id={noteId}
                    className="mt-4 inline-block text-[12px] font-bold uppercase tracking-[0.08em] text-pp-orange"
                    data-testid={`pricing-trust-note-${i + 1}`}
                  >
                    {item.note}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ============================ FAQ ============================ */}
      <section
        style={{ background: "var(--pp-v3-surface)" }}
        data-testid="pricing-faq-section"
      >
        <div className="pp-wrap py-16 md:py-24 grid items-start gap-10 md:grid-cols-[1fr_1.4fr] md:gap-16">
          <div>
            <span className="pp-kick" data-testid="pricing-faq-kick">
              Pricing FAQ
            </span>
            <h2 className="pp-h2 mt-3" data-testid="pricing-faq-headline">
              Quick answers to the obvious questions.
            </h2>
          </div>
          <ul
            className="list-none m-0 p-0 divide-y divide-pp-line"
            data-testid="pricing-faq-list"
          >
            {PRICING_FAQ.map((item, i) => (
              <li
                key={i}
                className="py-5 first:pt-0 last:pb-0"
                data-testid={`pricing-faq-item-${i + 1}`}
              >
                <h3 className="text-[17px] font-bold text-pp-slate">
                  {item.q}
                </h3>
                <p className="mt-2 text-[15px] text-pp-text-soft leading-relaxed">
                  {item.a}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============================ CTA BAND ============================ */}
      <section
        style={{ background: "var(--pp-purple)" }}
        data-testid="pricing-cta-section"
      >
        <div className="pp-wrap py-16 md:py-20 text-center">
          <h2
            className="text-white font-extrabold text-[28px] md:text-[40px] leading-tight max-w-3xl mx-auto"
            data-testid="pricing-cta-headline"
          >
            {PRICING_CTA.headline}
          </h2>
          <p
            className="mt-4 text-white/85 text-[16px] md:text-[18px] max-w-xl mx-auto"
            data-testid="pricing-cta-sub"
          >
            {PRICING_CTA.sub}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to={PRICING_CTA.customer_to}
              className="pp-btn"
              style={{ background: "white", color: "var(--pp-slate-2)" }}
              data-testid="pricing-cta-customer"
            >
              {PRICING_CTA.customer_label}
            </Link>
            <Link
              to={PRICING_CTA.partner_to}
              className="pp-btn pp-btn-primary"
              data-testid="pricing-cta-partner"
            >
              {PRICING_CTA.partner_label}
              <ArrowRight className="arrow h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
      <WinnerSlotCard winnerRank={5} />
     </div>
    </PublicPageShell>
  );
}

/* ---------------------------------------------------------------------------
   AudienceToggle — segmented pill that switches the Pricing page between
   the dog-owner and PooPartner content slices. State lives in the parent
   `Pricing` component; this component is purely controlled.

   Active segment styling matches the slate-on-orange WCAG-AA standard
   established by the Feb-2026 brown-CTA sweep (`text-pp-cta-text` on
   `bg-pp-orange` = 5.21:1 against `#F89623`).

   Mobile (<640px): container goes full-width, both buttons split 50/50
   via `flex-1`. Desktop: container shrinks to intrinsic width, buttons
   sit at their natural padding.

   ARIA: `role="group"` + `aria-pressed` per button. Not a tablist —
   there's no explicit `tabpanel`, so the pressed-button semantic is the
   honest contract.
   --------------------------------------------------------------------------- */
function AudienceToggle({ view, onChange }) {
  const segments = [
    { id: "partners", label: "For PooPartners" },
    { id: "owners", label: "For Dog Owners" },
  ];
  return (
    <div
      role="group"
      aria-label="Pricing audience"
      className="flex w-full max-w-md sm:w-auto sm:inline-flex p-1 rounded-full border border-pp-line bg-pp-v3-bg"
      style={{ boxShadow: "var(--pp-shadow)" }}
      data-testid="pricing-audience-toggle"
    >
      {segments.map((seg) => {
        const active = view === seg.id;
        return (
          <button
            key={seg.id}
            type="button"
            onClick={() => onChange(seg.id)}
            aria-pressed={active}
            data-testid={`pricing-audience-${seg.id}`}
            className={
              "flex-1 sm:flex-initial rounded-full px-5 sm:px-7 py-2.5 text-[14px] sm:text-[15px] font-bold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-pp-orange focus-visible:ring-offset-2 " +
              (active
                ? "bg-pp-orange text-pp-cta-text"
                : "text-pp-text-soft hover:text-pp-slate")
            }
          >
            {seg.label}
          </button>
        );
      })}
    </div>
  );
}
